import { useState, useCallback } from 'react'
import axios, { type AxiosError } from 'axios'
import { type CustomError, useApiUtils } from './useApiUtils'
import { type Configuration } from '../core/providers/ConfigurationContext'
import {
  type TelemetryType,
  type CreateTrackSessionRequest,
  type LapDto,
  type TrackSessionDto,
} from '@pitbrain/shared'
import { type RawTelemetrySource } from '../features/upload/rawTelemetrySource'

// Extend your hook's return type to include error handling
export interface TrackSessionApiReturnType {
  saveTrackSession: (
    request: CreateTrackSessionRequest
  ) => Promise<string | null>
  getSharedLapsForTrackSession: (
    tracksession: string
  ) => Promise<LapDto[] | null>
  getAllTrackSessions: () => Promise<TrackSessionDto[]>
  getTrackSessionByName: (
    trackSessionName: string,
    telemetryTypes: TelemetryType[]
  ) => Promise<TrackSessionDto | null>
  getTrackSession: (
    trackSessionId: string,
    telemetryTypes: TelemetryType[]
  ) => Promise<TrackSessionDto | null>
  storeSessionTelemetry: (
    trackSessionId: string,
    source: RawTelemetrySource
  ) => Promise<boolean>
  error: CustomError | null
}

export const useTrackSessionApi = (
  bffConfig: Configuration | null
): TrackSessionApiReturnType => {
  const [error, setError] = useState<CustomError | null>(null)
  const { getAuthToken, handleError, performApiCall } = useApiUtils(bffConfig)
  const resetErrors = () => {
    setError(null)
  }
  const saveTrackSession = useCallback(
    async (request: CreateTrackSessionRequest): Promise<string | null> => {
      if (!bffConfig) return null
      try {
        const token = await getAuthToken()
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/tracksession`,
          request,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )
        return await Promise.resolve(response.data)
      } catch (error) {
        const axiosError = error as AxiosError
        if (axiosError.response?.status === 409) {
          setError({
            error: true,
            message:
              'A session with this name already exists on the server. Use LOAD to open it instead of saving again.',
            code: 409,
          })
          return null
        }
        const handledError = handleError(axiosError)
        setError(handledError)
        return null
      }
    },
    [bffConfig, getAuthToken]
  )

  const getSharedLapsForTrackSession = useCallback(
    async (tracksession: string): Promise<LapDto[] | null> => {
      // const unknownTrack = { name: 'Unknown', finishLine: [], id: "00000000-0000-0000-0000-000000000000" };
      if (!bffConfig) return null
      try {
        const token = await getAuthToken()
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/tracksession/${tracksession}/laps/shared`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )

        return response.data as LapDto[]
      } catch (error) {
        const handledError = handleError(error as AxiosError)
        setError(handledError)
        return null
      }
    },
    [bffConfig, getAuthToken]
  )

  const getAllTrackSessions = useCallback(async (): Promise<
    TrackSessionDto[]
  > => {
    if (!bffConfig) return []
    try {
      const token = await getAuthToken()
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/tracksession`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      return response.data as TrackSessionDto[]
    } catch (error) {
      const handledError = handleError(error as AxiosError)
      setError(handledError)
      return []
    }
  }, [bffConfig, getAuthToken])

  const getTrackSessionByName = useCallback(
    async (
      trackSessionName: string,
      telemetryTypes: TelemetryType[] = []
    ): Promise<TrackSessionDto | null> => {
      if (!bffConfig) return null
      try {
        const token = await getAuthToken()
        const telemetryQuery = telemetryTypes
          .map((t) => `telemetryTypes=${t}`)
          .join('&')
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/tracksession?name=${trackSessionName}${telemetryTypes.length ? '&' : ''}${telemetryQuery}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )

        return response.data as TrackSessionDto
      } catch (error) {
        const axiosError = error as AxiosError
        const handledError = handleError(axiosError)
        setError(handledError)
        if (axiosError.response?.status === 404) {
          // Not found is the safe "no existing session" signal - callers
          // treat null as "safe to treat as new".
          return null
        }
        // Any other failure means we could NOT verify whether the session
        // already exists. Throw so the caller (upload processors, awaited
        // inside useFileParser.parseFile's try/catch) aborts the upload
        // instead of silently proceeding as if it were a new session.
        throw new Error(
          'Could not verify whether this session already exists on the server. Please try again.'
        )
      }
    },
    [bffConfig, getAuthToken]
  )

  const getTrackSession = useCallback(
    async (
      trackSessionId: string,
      telemetryTypes: TelemetryType[] = []
    ): Promise<TrackSessionDto | null> => {
      if (!bffConfig) return null
      try {
        const token = await getAuthToken()
        const telemetryQuery = telemetryTypes
          .map((t) => `telemetryTypes=${t}`)
          .join('&')
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/tracksession/${trackSessionId}?${telemetryQuery}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )

        return response.data as TrackSessionDto
      } catch (error) {
        const handledError = handleError(error as AxiosError)
        setError(handledError)
        return null
      }
    },
    [bffConfig, getAuthToken]
  )

  /**
   * Archive the stream this session was parsed from.
   *
   * Never surfaces an error: the session is already saved and is the primary
   * product, so a failure here costs the ability to reprocess later, not the
   * upload the rider just performed.
   */
  const storeSessionTelemetry = useCallback(
    async (
      trackSessionId: string,
      source: RawTelemetrySource
    ): Promise<boolean> => {
      if (!bffConfig) return false
      try {
        const token = await getAuthToken()
        const form = new FormData()
        form.append(
          'file',
          new File([source.content], source.fileName, {
            type: source.contentType,
          })
        )

        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/file/session/${trackSessionId}/telemetry`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        return true
      } catch (error) {
        console.warn(
          `Could not archive raw telemetry for session ${trackSessionId}; the session itself was saved.`,
          error
        )
        return false
      }
    },
    [bffConfig, getAuthToken]
  )

  return {
    storeSessionTelemetry,
    saveTrackSession: performApiCall(saveTrackSession, resetErrors),
    getSharedLapsForTrackSession: performApiCall(
      getSharedLapsForTrackSession,
      resetErrors
    ),
    getAllTrackSessions: performApiCall(getAllTrackSessions, resetErrors),
    getTrackSessionByName: performApiCall(getTrackSessionByName, resetErrors),
    getTrackSession: performApiCall(getTrackSession, resetErrors),
    error,
  }
}
